import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ApplicationForUser } from '../../../models/borrower-portal/application-for-user-model';
import { ApplicationTaskModel, BorrowerTaskStatus, BorrowerTaskType } from '../../../models/borrower-portal/application-task-model';
import { LinkedDocument } from '../view-document/view-document.component';
import { TasksUtils } from 'projects/shared/tasks.utils';
import { TaskNotApplicableDialogComponent } from '../task-not-applicable-dialog/task-not-applicable-dialog.component';
import { Constants } from '../../../services/constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ApplicationContextService } from '../../../services/application-context.service';
import { BasePortalService } from '../../../services/base-portal.service';
import { DocFileType } from '../../../models/borrower-portal/linked-document-model';

@Component({
    selector: 'tasks-normal',
    templateUrl: './tasks-normal.component.html',
    styleUrls: ['./tasks-normal.component.scss'],
    standalone: false
})
export class TasksNormalComponent implements OnInit, OnChanges, OnDestroy {

  @Input() tasks: ApplicationTaskModel[] = [];
  @Input() application: ApplicationForUser;
  @Input() isBorrowerPortal: boolean = true;
  @Input() isSubmitted: boolean;

  @Input() enabledTaskIdForESign: number;

  @Input() doNotEnforceChronologicalOrder: boolean = false;

  @Output() openPaymentInfoClicked = new EventEmitter<ApplicationTaskModel>();
  @Output() openEsignDocumentClicked = new EventEmitter<ApplicationTaskModel>();
  @Output() openAttachedDocumentsClicked = new EventEmitter<ApplicationTaskModel>();
  @Output() openAddFilesClicked = new EventEmitter<ApplicationTaskModel>();
  @Output() taskMarkedAsNotApplicable = new EventEmitter<ApplicationTaskModel>();
  @Output() openCreditAuthConsentClicked = new EventEmitter<ApplicationTaskModel>();


  get filteredSortedTasks() {
    return (this.tasks ?? [])
      .filter(t => !!t?.taskId)
      .sort((a, b) => (a.taskId ?? 0) - (b.taskId ?? 0));
  }

  protected isCompanyPhm: boolean = false;

  /** Tracks which tasks have the "Not Applicable" checkbox/button engaged (keyed by taskId) */
  protected taskNotApplicableIds = new Set<number>();

  /** Tracks which task cards are expanded on mobile */
  protected expandedTaskIds = new Set<number>();

  /** Tracks which tasks have their borrower-facing-note history expanded (keyed by taskId) */
  protected expandedNoteTaskIds = new Set<number>();

  /** Tracks which tasks are currently loading their note history (keyed by taskId) */
  protected noteLoadingTaskIds = new Set<number>();

  /** Loaded note history per task (keyed by taskId) */
  protected otherNotesByTaskId = new Map<number, any[]>();

  toggleTaskExpanded(taskId: number): void {
    if (this.expandedTaskIds.has(taskId)) {
      this.expandedTaskIds.delete(taskId);
    } else {
      this.expandedTaskIds.add(taskId);
    }
  }

  isTaskExpanded(taskId: number): boolean {
    return this.expandedTaskIds.has(taskId);
  }

  onSummaryKeydown(event: KeyboardEvent, taskId: number): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      if (event.key !== 'Enter') {
        event.preventDefault();
      }
      this.toggleTaskExpanded(taskId);
    }
  }

  onNoteToggleKeydown(event: KeyboardEvent, taskId: number): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      if (event.key !== 'Enter') {
        event.preventDefault();
      }
      this.toggleExpand(taskId);
    }
  }

  isTaskNotApplicable(task: ApplicationTaskModel): boolean {
    return this.taskNotApplicableIds.has(task.taskId);
  }

  isNoteExpanded(taskId: number): boolean {
    return this.expandedNoteTaskIds.has(taskId);
  }

  isNoteLoading(taskId: number): boolean {
    return this.noteLoadingTaskIds.has(taskId);
  }

  getOtherNotes(taskId: number): any[] {
    return this.otherNotesByTaskId.get(taskId) ?? [];
  }

  private _contextSubscription: Subscription;

  constructor(private readonly _modalService: NgbModal,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _portalService: BasePortalService
  ) { }


  ngOnInit(): void {
    this._contextSubscription = this._applicationContextService.context.subscribe(context => {
      this.isCompanyPhm = context.isCompanyPHM;
      this.populateAttachedDocuments();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tasks']) {
      this.populateAttachedDocuments();
    }
  }

  private populateAttachedDocuments(): void {
    if (!this.tasks) return;

    this.tasks.forEach(t => {
      t['attachedDocuments'] = [];
      if (t.linkedDocuments) {
        t.linkedDocuments.forEach(d => {
          if (!d.documentGuid) {
            return;
          }
          const linkedDocument: LinkedDocument = {
            fileGuid: d.documentGuid.toString(),
            fileName: d.fileName,
            note: d.borrowerNote,
            dateInserted: d.dateInserted ? new Date(d.dateInserted.endsWith('Z') ? d.dateInserted : d.dateInserted + 'Z') : null,
            insertedByName: d.insertedByName,
            mimeType: '',
            docFileType: d.docFileType
          }
          t['attachedDocuments'].push(linkedDocument);
        });

        t['attachedDocuments'] = t['attachedDocuments'].sort((a, b) => {
          if (a.docFileType === DocFileType.EsignedCertOfCompletion && b.docFileType !== DocFileType.EsignedCertOfCompletion) return 1;
          if (a.docFileType !== DocFileType.EsignedCertOfCompletion && b.docFileType === DocFileType.EsignedCertOfCompletion) return -1;
          return 0;
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._contextSubscription?.unsubscribe();
  }

  onTaskNotApplicableChanged = (task: ApplicationTaskModel, notApplicable: boolean) => {
    if (notApplicable) {
      this.taskNotApplicableIds.add(task.taskId);
      const modalRef = this._modalService.open(TaskNotApplicableDialogComponent, Constants.modalOptions.medium);
      modalRef.componentInstance.task = task;
      modalRef.result.then(result => {
        this.taskMarkedAsNotApplicable.emit(task);
        this.taskNotApplicableIds.delete(task.taskId);
      }, error => {
        this.taskNotApplicableIds.delete(task.taskId);
      });
    } else {
      this.taskNotApplicableIds.delete(task.taskId);
    }
  }

  openAddFiles(task: ApplicationTaskModel) {
    this.openAddFilesClicked.emit(task);
  }

  goToLink(url: string) {
    window.open(url, "_blank");
  }

  shouldShowEsignDocumentEmailTextMessage(task: ApplicationTaskModel): boolean {
    return (task.borrowerTaskType === BorrowerTaskType.LosEsign
      || task.borrowerTaskType === BorrowerTaskType.EsignDocument)
      && task.borrowerTaskStatus !== BorrowerTaskStatus.Completed
      && task.signingLinkSentInEmail
  }

  // Title for a task with no doc type or description, so its card never renders
  // without a heading.
  fallbackTaskTitleKey(task: ApplicationTaskModel): string {
    return task.borrowerTaskType === BorrowerTaskType.LosEsign || task.borrowerTaskType === BorrowerTaskType.EsignDocument
      ? 'portal.tasks.eSignDocument'
      : 'portal.tasks.task';
  }

  shouldShowEsignDocumentBtn(task: ApplicationTaskModel): boolean {
    return TasksUtils.shouldShowEsignDocument(task, this.isBorrowerPortal);
  }

  openPaymentInfo = (task: ApplicationTaskModel) => {
    this.openPaymentInfoClicked.emit(task);
  }

  isEsignTaskDisabled(task: ApplicationTaskModel): boolean {
    return !this.doNotEnforceChronologicalOrder
      && this.enabledTaskIdForESign != task.taskId
      && task.borrowerTaskType === BorrowerTaskType.LosEsign;
  }

  openEsignDocument = (task: ApplicationTaskModel) => {
    if (this.isEsignTaskDisabled(task)) {
      return;
    }
    this.openEsignDocumentClicked.emit(task);
  }

  openCreditAuthConsent = (task: ApplicationTaskModel) => {
    this.openCreditAuthConsentClicked.emit(task);
  }

  shouldShowCreditAuthBtn(task: ApplicationTaskModel): boolean {
    return task.borrowerTaskType === BorrowerTaskType.CreditAuthorization
      && task.borrowerTaskStatus !== BorrowerTaskStatus.Completed
      && this.isBorrowerPortal;
  }

  protected toggleExpand(taskId: number) {
    if (this.expandedNoteTaskIds.has(taskId)) {
      this.expandedNoteTaskIds.delete(taskId);
      return;
    }
    this.expandedNoteTaskIds.add(taskId);

    // Already loaded once, or a request is already in flight — don't fire another.
    if (this.otherNotesByTaskId.has(taskId) || this.noteLoadingTaskIds.has(taskId)) {
      return;
    }

    this.noteLoadingTaskIds.add(taskId);
    this._portalService.getTaskMessages(taskId).subscribe({
      next: (notes) => {
        const sortedNotes = notes.slice().sort(
          (first, second) => Date.parse(String(second.postTime)) - Date.parse(String(first.postTime))
        );
        this.otherNotesByTaskId.set(taskId, sortedNotes.slice(1));
        this.noteLoadingTaskIds.delete(taskId);
      },
      error: () => {
        this.noteLoadingTaskIds.delete(taskId);
        this.expandedNoteTaskIds.delete(taskId);
      }
    });
  }

  protected taskStatusLabel(task: ApplicationTaskModel): string {
    switch (task?.borrowerTaskStatus) {
      case BorrowerTaskStatus.Pending:
        return 'portal.tasks.pending';
      case BorrowerTaskStatus.Submitted:
        return 'portal.tasks.submitted';
      case BorrowerTaskStatus.Rejected:
        return 'portal.tasks.rejected';
      case BorrowerTaskStatus.Completed:
        return 'portal.tasks.completed';
      case BorrowerTaskStatus.Approved:
        return 'portal.tasks.approved';
      default:
        return 'portal.tasks.status';
    }
  }



}
